import React, { useState, useEffect } from 'react';
import { Key, Check, ChevronRight, AlertCircle, Infinity as InfinityIcon, Crown, Zap, Tag, Flame, X, Lock } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { showDiscordRequired } from './DiscordRequiredModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LICENCE_TIERS = [
  { id: 'licence_1',        uses: 1,    price: 14.99, popular: false, lifetime: false },
  { id: 'licence_2',        uses: 2,    price: 27.99, popular: false, lifetime: false },
  { id: 'licence_3',        uses: 3,    price: 39.99, popular: true,  lifetime: false },
  { id: 'licence_4',        uses: 4,    price: 49.99, popular: false, lifetime: false },
  { id: 'licence_5',        uses: 5,    price: 59.99, popular: false, lifetime: false },
  { id: 'licence_lifetime', uses: 999,  price: 79.99, popular: false, lifetime: true  },
];

const LicencesPage = () => {
  const { isAuthenticated, loginWithDiscord, token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [showPaypalSuccess, setShowPaypalSuccess] = useState(false);
  const [paypalTicketData, setPaypalTicketData] = useState(null);
  const [hasLicence, setHasLicence] = useState(false);
  const [error, setError] = useState(null);
  const [srvPrices, setSrvPrices] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/pricing`).then(r => r.json()).then(d => setSrvPrices(d.prices)).catch(() => {});
  }, []);

  const licenceTiers = LICENCE_TIERS.map(t => ({ ...t, price: srvPrices?.[t.id] ?? t.price }));

  // Client = has licence OR has Discord Client role
  const isClient = hasLicence || (user && user.is_client === true);

  // Client discount config (per-tier promo set by admin in /admin → Promos Clients)
  const [clientDiscount, setClientDiscount] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/client-discount`)
      .then(r => r.json())
      .then(d => setClientDiscount(d && d.enabled ? d : null))
      .catch(() => setClientDiscount(null));
  }, []);

  // Compute client discount % for a given tier (only if user is Client)
  const getTierClientDiscount = (tierId) => {
    if (!isClient || !clientDiscount || !clientDiscount.enabled) return null;
    const rule = clientDiscount.rules?.[tierId];
    if (!rule || !rule.enabled || !rule.value) return null;
    return rule; // { enabled, type: 'percent'|'fixed', value }
  };

  // Flash sale (server-side discount)
  const [flashSale, setFlashSale] = useState(null);
  const [flashCountdown, setFlashCountdown] = useState('');

  // Promo code input
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // {code, discount_percent}
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoError, setPromoError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !token) { setHasLicence(false); return; }
    fetch(`${API_URL}/api/user/licence-status`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setHasLicence(d.has_licence === true))
      .catch(() => setHasLicence(false));
  }, [isAuthenticated, token]);

  // Fetch active flash sale and only show if it applies to "licence"
  useEffect(() => {
    fetch(`${API_URL}/api/flash-sale`)
      .then(r => r.json())
      .then(d => {
        if (d && d.active) {
          const applies = !d.services || d.services.length === 0 || d.services.includes('licence');
          if (applies) setFlashSale(d);
        }
      })
      .catch(() => {});
  }, []);

  // Live countdown
  useEffect(() => {
    if (!flashSale) return;
    const tick = () => {
      const ms = new Date(flashSale.end_time).getTime() - Date.now();
      if (ms <= 0) { setFlashSale(null); return; }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setFlashCountdown(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [flashSale]);

  // Effective discount = max(flashSale, appliedPromo)
  const flashDiscount = flashSale ? flashSale.discount_percent : 0;
  const promoDiscount = appliedPromo ? appliedPromo.discount_percent : 0;
  const effectiveDiscount = Math.max(flashDiscount, promoDiscount);
  const discountSource = promoDiscount > flashDiscount ? 'promo' : (flashDiscount > 0 ? 'flash' : null);

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoChecking(true);
    setPromoError(null);
    try {
      const res = await fetch(`${API_URL}/api/promo-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, service_type: 'licence_1' }), // any licence_* works for scope check
      });
      const data = await res.json();
      if (!res.ok || !data.valid) throw new Error(data.detail || 'Code invalide');
      setAppliedPromo({ code: data.code, discount_percent: data.discount_percent });
      setPromoInput('');
    } catch (e) {
      setPromoError(e.message);
    } finally {
      setPromoChecking(false);
    }
  };

  const clearPromo = () => { setAppliedPromo(null); setPromoError(null); };

  const handleOrder = async (tierId, method = 'stripe') => {
    if (!isAuthenticated) { loginWithDiscord(); return; }
    setLoading(`${tierId}_${method}`);
    setError(null);
    try {
      const promoToSend = appliedPromo ? appliedPromo.code : '';
      if (method === 'stripe') {
        const res = await fetch(`${API_URL}/api/create-checkout-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ service_type: tierId, affiliate_code: '', promo_code: promoToSend }),
        });
        const data = await res.json();
        if (res.status === 403 && (data.detail === 'not_in_discord' || data.detail === 'discord_banned')) {
          showDiscordRequired(data.detail);
          return;
        }
        if (res.status === 403 && data.detail === 'client_only_licence') {
          throw new Error("Les licences sont réservées aux clients AqmOpti (rôle client requis sur le Discord). Réalise d'abord une optimisation pour y accéder. 💙");
        }
        if (data.url) window.location.href = data.url;
        else throw new Error(data.detail || 'Erreur Stripe');
      } else {
        const tier = licenceTiers.find(t => t.id === tierId);
        const label = tier?.lifetime ? 'Lifetime' : `${tier.uses} utilisation${tier.uses > 1 ? 's' : ''}`;
        // Use effective discount client-side so the ticket reflects what the user sees
        const finalPrice = +(tier.price * (1 - effectiveDiscount / 100)).toFixed(2);
        const res = await fetch(`${API_URL}/api/create-payment-ticket`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            payment_method: method,
            service_type: tierId,
            service_name: `Licence AqmOpti — ${label}`,
            total_price: finalPrice,
            options: {},
            affiliate_code: '',
            promo_code: promoToSend,
            original_price: tier?.price || 0,
            discount_percent: effectiveDiscount,
          }),
        });
        const data = await res.json();
        if (res.ok) {
          setPaypalTicketData({
            method,
            serviceName: `Licence AqmOpti — ${label}`,
            totalPrice: method === 'paysafecard' ? finalPrice + 5 : finalPrice,
            discord_url: data.discord_url,
          });
          setShowPaypalSuccess(true);
        } else {
          if (res.status === 403 && data.detail === 'client_only_licence') {
            throw new Error("Les licences sont réservées aux clients AqmOpti (rôle client requis sur le Discord). Réalise d'abord une optimisation pour y accéder. 💙");
          }
          throw new Error(data.detail || 'Erreur ticket');
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen py-12 px-4 relative overflow-hidden" data-testid="licences-page">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[120px] pointer-events-none opacity-25 bg-cyan-500" />
      <div className="absolute top-40 right-0 w-[400px] h-[400px] rounded-full blur-[120px] pointer-events-none opacity-10 bg-indigo-500" />

      {/* Accès réservé aux clients (rôle client Discord) : on gate directement la page */}
      {(!isAuthenticated || (user && !(user.is_client === true || user.is_admin === true))) ? (
        <div className="relative max-w-lg mx-auto mt-10" data-testid="licences-client-only-gate">
          <div className="rounded-2xl border border-cyan-900/40 bg-zinc-950/80 backdrop-blur-sm p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mx-auto mb-5">
              <Lock className="w-8 h-8 text-cyan-400" strokeWidth={1.5} />
            </div>
            <p className="text-cyan-400/80 text-[11px] font-medium uppercase tracking-[0.18em] mb-2">Licences AqmOpti</p>
            <h1 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight mb-3">
              Réservé aux <span className="text-cyan-400">clients</span>
            </h1>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              L'achat de licences (activations supplémentaires) est réservé aux clients AqmOpti.
              {' '}Réalise d'abord une <span className="text-zinc-200 font-medium">optimisation Windows ou Pack Complet</span> pour obtenir le rôle client sur le Discord et débloquer l'achat de licences. 💙
            </p>
            {!isAuthenticated ? (
              <button onClick={loginWithDiscord}
                data-testid="licences-login-btn"
                className="inline-flex items-center gap-2 px-5 py-3 bg-cyan-500 hover:bg-cyan-400 text-zinc-950 text-sm font-semibold rounded-xl transition-colors">
                Se connecter avec Discord
              </button>
            ) : (
              <button onClick={() => navigate('/services')}
                data-testid="licences-see-services-btn"
                className="inline-flex items-center gap-2 px-5 py-3 bg-cyan-500 hover:bg-cyan-400 text-zinc-950 text-sm font-semibold rounded-xl transition-colors">
                Voir les optimisations
                <ChevronRight className="w-4 h-4" strokeWidth={2} />
              </button>
            )}
          </div>
        </div>
      ) : (
      <div className="relative max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-10 pb-6 border-b border-zinc-800/60">
          <p className="text-cyan-400/80 text-[11px] font-medium uppercase tracking-[0.18em] mb-2 flex items-center gap-1.5">
            <Zap className="w-3 h-3" />
            Licences AqmOpti
          </p>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
                Activations du <span className="text-cyan-400">logiciel</span>
              </h1>
              <p className="text-zinc-400 text-sm max-w-xl leading-relaxed">
                Chaque licence s'additionne à tes utilisations existantes. Récupère ta clé sur Discord avec{' '}
                <code className="bg-cyan-500/10 border border-cyan-500/20 px-1.5 py-0.5 rounded text-cyan-300 font-mono text-[11px]">/cle</code>.
              </p>
            </div>
            {hasLicence && (
              <button onClick={() => navigate('/ma-licence')}
                data-testid="go-to-my-licence-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-sm font-medium rounded-lg transition-colors">
                <Key className="w-3.5 h-3.5" strokeWidth={1.5} />
                Voir ma licence
                <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-6 flex items-start gap-2.5 px-4 py-3 bg-red-500/5 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="licences-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <span>{error}</span>
          </div>
        )}

        {/* Flash sale banner */}
        {flashSale && (
          <div className="mb-6 relative overflow-hidden rounded-2xl border border-orange-500/30 bg-gradient-to-r from-orange-950/40 via-zinc-950 to-zinc-950 px-5 py-4" data-testid="flash-sale-banner">
            <div className="absolute -right-12 -top-12 w-48 h-48 bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />
            <div className="relative flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-orange-500/15 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
                  <Flame className="w-5 h-5 text-orange-300" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <p className="text-orange-300 text-xs font-bold uppercase tracking-widest mb-0.5">Vente flash</p>
                  <p className="text-white font-semibold tracking-tight truncate">{flashSale.title} · <span className="text-orange-300">-{flashSale.discount_percent}%</span></p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-orange-100 font-mono text-sm bg-black/30 px-3 py-1.5 rounded-lg border border-orange-500/20">
                <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                <span data-testid="flash-countdown">{flashCountdown}</span>
              </div>
            </div>
          </div>
        )}

        {/* Promo code input */}
        <div className="mb-6 flex items-center gap-2 flex-wrap">
          {appliedPromo ? (
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-300 text-sm" data-testid="applied-promo">
              <Tag className="w-3.5 h-3.5" />
              <span className="font-mono font-semibold">{appliedPromo.code}</span>
              <span className="text-emerald-400/80">−{appliedPromo.discount_percent}%</span>
              <button onClick={clearPromo} className="ml-1 hover:text-emerald-200" data-testid="remove-promo-btn">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-1 sm:flex-initial">
                <input
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && applyPromo()}
                  placeholder="Code promo"
                  className="bg-zinc-950 border border-zinc-800 focus:border-cyan-500/40 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 font-mono uppercase tracking-wider focus:outline-none w-full sm:w-44 transition-colors"
                  data-testid="promo-input"
                />
                <button
                  onClick={applyPromo}
                  disabled={promoChecking || !promoInput.trim()}
                  className="px-3 py-2 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  data-testid="apply-promo-btn">
                  {promoChecking ? '…' : 'Appliquer'}
                </button>
              </div>
              {promoError && <span className="text-red-400 text-xs">{promoError}</span>}
            </>
          )}
        </div>

        {/* Tiers grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {licenceTiers.map((tier) => {
            const clientRule = getTierClientDiscount(tier.id);
            // Compute the exact client-discounted price (fixed € applies exactly, no % rounding)
            let clientPrice = tier.price;
            let clientLabel = null;
            if (clientRule) {
              if (clientRule.type === 'percent') {
                clientPrice = +(tier.price * (1 - clientRule.value / 100)).toFixed(2);
                clientLabel = `−${parseFloat(clientRule.value)}%`;
              } else {
                clientPrice = Math.max(0, +(tier.price - clientRule.value).toFixed(2));
                clientLabel = `−${(+clientRule.value).toFixed(2).replace('.', ',')}€`;
              }
            }
            // The best (lowest) price wins between client promo and flash/promo code
            const promoPrice = +(tier.price * (1 - effectiveDiscount / 100)).toFixed(2);
            const useClient = clientPrice < promoPrice;
            const finalPrice = Math.min(clientPrice, promoPrice);
            const tierSource = useClient ? 'client' : discountSource;
            const tierLabel = useClient ? clientLabel : (effectiveDiscount > 0 ? `−${effectiveDiscount}%` : null);
            return (
              <LicenceCard
                key={tier.id}
                tier={tier}
                loading={loading}
                onOrder={handleOrder}
                finalPrice={finalPrice}
                discountSource={tierSource}
                discountLabel={tierLabel}
              />
            );
          })}
        </div>

        {/* Trust row */}
        <div className="mt-14 pt-8 border-t border-zinc-800/60 grid sm:grid-cols-3 gap-6">
          <TrustCard
            icon={<Check className="w-4 h-4" />}
            title="Paiement sécurisé"
            text="Stripe, PayPal, Paysafecard. Aucune carte n'est stockée."
          />
          <TrustCard
            icon={<Key className="w-4 h-4" />}
            title="Lié à ton Discord"
            text="La clé est rattachée à ton compte. Aucun transfert possible."
          />
          <TrustCard
            icon={<Zap className="w-4 h-4" />}
            title="Cumul automatique"
            text="Les nouvelles utilisations s'ajoutent à ta licence existante."
          />
        </div>

      </div>
      )}

      {/* PayPal/PSC success modal */}
      {showPaypalSuccess && paypalTicketData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setShowPaypalSuccess(false)}>
          <div className="bg-zinc-950 border border-cyan-500/20 rounded-xl max-w-md w-full p-6 shadow-[0_30px_80px_-20px_rgba(34,211,238,0.3)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
                <Check className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-white font-semibold tracking-tight">Ticket créé</h3>
                <p className="text-zinc-500 text-xs">{paypalTicketData.serviceName}</p>
              </div>
            </div>
            <p className="text-zinc-400 text-sm mb-5 leading-relaxed">
              Un ticket Discord a été ouvert. Termine ton paiement <strong className="text-zinc-200">{paypalTicketData.method === 'paypal' ? 'PayPal' : 'Paysafecard'}</strong> de <strong className="text-cyan-300">{paypalTicketData.totalPrice.toFixed(2)}€</strong>, puis envoie la preuve dans le ticket.
            </p>
            <div className="flex gap-2">
              {paypalTicketData.discord_url && (
                <a href={paypalTicketData.discord_url} target="_blank" rel="noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] text-white text-sm font-medium rounded-lg transition-colors">
                  Aller au ticket
                </a>
              )}
              <button onClick={() => setShowPaypalSuccess(false)}
                className="px-4 py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 text-sm font-medium rounded-lg transition-colors">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Card ────────────────────────────────────────────────────────────────────
const LicenceCard = ({ tier, loading, onOrder, finalPrice = null, discountSource = null, discountLabel = null }) => {
  const isLifetime = tier.lifetime;
  const isPopular = tier.popular;
  const label = isLifetime ? 'Lifetime' : `${tier.uses} utilisation${tier.uses > 1 ? 's' : ''}`;
  const discountedPrice = finalPrice != null ? finalPrice : tier.price;
  const showDiscount = discountedPrice < tier.price;
  const unitPrice = !isLifetime ? (discountedPrice / tier.uses) : null;

  const accent = isLifetime ? 'amber' : isPopular ? 'cyan' : 'zinc';

  const cardStyles = {
    amber: 'bg-gradient-to-br from-amber-950/30 via-zinc-950 to-zinc-950 border-amber-500/30 hover:border-amber-400/50',
    cyan:  'bg-gradient-to-br from-cyan-950/30 via-zinc-950 to-zinc-950 border-cyan-500/30 hover:border-cyan-400/50',
    zinc:  'bg-zinc-950/50 border-zinc-800 hover:border-zinc-700',
  };

  const cardShadow = {
    amber: 'shadow-[0_0_0_1px_rgba(251,191,36,0.05),0_20px_50px_-20px_rgba(251,191,36,0.25)]',
    cyan:  'shadow-[0_0_0_1px_rgba(34,211,238,0.08),0_20px_50px_-20px_rgba(34,211,238,0.3)]',
    zinc:  '',
  };

  return (
    <div
      data-testid={`licence-card-${tier.id}`}
      className={`relative flex flex-col rounded-xl p-6 border transition-all duration-300 hover:-translate-y-1 ${cardStyles[accent]} ${cardShadow[accent]}`}
    >
      {/* Badge */}
      {isPopular && (
        <div className="absolute -top-3 left-6 inline-flex items-center gap-1 px-3 py-1 bg-cyan-500 text-zinc-950 text-[10px] font-bold uppercase tracking-wider rounded-full shadow-lg shadow-cyan-500/40">
          <Zap className="w-3 h-3" />
          Populaire
        </div>
      )}
      {isLifetime && (
        <div className="absolute -top-3 left-6 inline-flex items-center gap-1 px-3 py-1 bg-amber-400 text-zinc-950 text-[10px] font-bold uppercase tracking-wider rounded-full shadow-lg shadow-amber-400/40">
          <Crown className="w-3 h-3" />
          Meilleure offre
        </div>
      )}

      {/* Title */}
      <div className="flex items-center gap-2.5 mb-5">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          isLifetime ? 'bg-amber-500/10 border border-amber-500/25' :
          isPopular ? 'bg-cyan-500/10 border border-cyan-500/25' :
          'bg-zinc-900 border border-zinc-800'
        }`}>
          {isLifetime
            ? <InfinityIcon className="w-4 h-4 text-amber-300" strokeWidth={1.5} />
            : <Key className={`w-4 h-4 ${isPopular ? 'text-cyan-300' : 'text-zinc-400'}`} strokeWidth={1.5} />}
        </div>
        <h3 className="text-white font-semibold text-base tracking-tight">{label}</h3>
      </div>

      {/* Price */}
      <div className="mb-5">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className={`text-4xl font-bold tracking-tight ${
            isLifetime ? 'text-amber-300' : isPopular ? 'text-cyan-300' : 'text-white'
          }`}>{(showDiscount ? discountedPrice : tier.price).toFixed(2)}</span>
          <span className="text-zinc-500 text-sm font-medium">€</span>
          {showDiscount && (
            <span className="text-zinc-500 line-through text-sm font-medium">{tier.price.toFixed(2)}€</span>
          )}
          {showDiscount && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
              discountSource === 'flash'
                ? 'bg-orange-500/15 border border-orange-500/30 text-orange-300'
                : discountSource === 'client'
                  ? 'bg-green-500/15 border border-green-500/30 text-green-300'
                  : 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-300'
            }`}>
              {discountSource === 'flash' ? <Flame className="w-2.5 h-2.5" /> : discountSource === 'client' ? <Crown className="w-2.5 h-2.5" /> : <Tag className="w-2.5 h-2.5" />}
              {discountLabel}
              {discountSource === 'client' && <span className="ml-0.5">Client</span>}
            </span>
          )}
        </div>
        {unitPrice && (
          <p className="text-zinc-500 text-xs mt-1.5">{unitPrice.toFixed(2)}€ <span className="text-zinc-600">/ utilisation</span></p>
        )}
        {isLifetime && (
          <p className="text-amber-400/70 text-xs mt-1.5">Activations illimitées à vie</p>
        )}
      </div>

      {/* Features */}
      <ul className="space-y-2.5 mb-6 flex-1">
        {[
          isLifetime ? 'Optimisations complètes Windows illimitées' : `${tier.uses} utilisation${tier.uses > 1 ? 's' : ''} = ${tier.uses} optimisation${tier.uses > 1 ? 's' : ''} complète${tier.uses > 1 ? 's' : ''} Windows`,
          'Clé liée à ton compte Discord',
          isLifetime ? 'Valable à vie' : "S'ajoute à ta licence existante",
          ...(isLifetime ? ["Bénéficie de toutes les prochaines mises à jour de l'opti"] : []),
        ].map((f) => (
          <li key={f} className="flex items-start gap-2 text-xs text-zinc-300">
            <Check className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${
              isLifetime ? 'text-amber-400' : isPopular ? 'text-cyan-400' : 'text-zinc-500'
            }`} strokeWidth={2} />
            <span className="leading-relaxed">{f}</span>
          </li>
        ))}
      </ul>

      {/* Actions — PayPal first (favored) */}
      <div className="space-y-2">
        <button
          data-testid={`licence-paypal-${tier.id}`}
          onClick={() => onOrder(tier.id, 'paypal')}
          disabled={!!loading}
          className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50
            ${isLifetime
              ? 'bg-amber-400 hover:bg-amber-300 text-zinc-950 shadow-lg shadow-amber-500/20'
              : isPopular
                ? 'bg-cyan-500 hover:bg-cyan-400 text-zinc-950 shadow-lg shadow-cyan-500/30'
                : 'bg-white text-zinc-900 hover:bg-zinc-200'
            }`}
        >
          Payer avec PayPal
        </button>
        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid={`licence-stripe-${tier.id}`}
            onClick={() => onOrder(tier.id, 'stripe')}
            disabled={loading === `${tier.id}_stripe`}
            className="py-2 rounded-lg text-xs font-medium bg-zinc-900/60 hover:bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-300 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            {loading === `${tier.id}_stripe`
              ? <span className="animate-spin w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full" />
              : <>Carte +1€</>}
          </button>
          <button
            data-testid={`licence-psc-${tier.id}`}
            onClick={() => onOrder(tier.id, 'paysafecard')}
            disabled={!!loading}
            className="py-2 rounded-lg text-xs font-medium bg-zinc-900/60 hover:bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-300 transition-colors disabled:opacity-50"
          >
            Paysafecard +5€
          </button>
        </div>
      </div>
    </div>
  );
};

const TrustCard = ({ icon, title, text }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-center text-cyan-400 flex-shrink-0">
      {icon}
    </div>
    <div>
      <p className="text-zinc-200 text-sm font-semibold mb-1">{title}</p>
      <p className="text-zinc-500 text-xs leading-relaxed">{text}</p>
    </div>
  </div>
);

export default LicencesPage;
